import type { Env } from "@/env-schema";

import { ApiClientError } from "@/api/api-client-error.ts";
import { tokenResponseSchema } from "@/api/backoffice/login/backoffice-external-login-api";
import {
  retrieveTokenDetailsFromLocalStorage,
  saveTokenDetailsInLocalStorage,
} from "@/lib/helpers/authentication-helper";
import { SessionExpiredError } from "@/lib/session-expired-error.ts";

export type TBackofficeEnv = Pick<Env, "VITE_VOLTACALL_SERVICE_ID" | "VITE_BACKOFFICE_BASE_URL">;

export async function reauthenticateOnSessionExpired(
  response: Response,
  request: Request,
  env: TBackofficeEnv,
): Promise<Response> {
  // If not a 401 error, return the response as-is
  if (response.status !== 401) {
    return response;
  }

  // Get the refresh token from local storage
  const tokenDetails = retrieveTokenDetailsFromLocalStorage();
  if (!tokenDetails) {
    return response;
  }

  const { refresh_token: refreshToken } = tokenDetails;

  try {
    // Call the refresh endpoint to get a new access token
    // TODO : Use generic sdk-generated call when it's available in the schema
    const refreshResponse = await fetch(`${env.VITE_BACKOFFICE_BASE_URL}/login/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!refreshResponse.ok) {
      if (refreshResponse.status === 401) {
        throw new SessionExpiredError(new Error("The refresh token was rejected"));
      }

      throw new ApiClientError({
        backend: "backoffice",
        classification: "technical",
        response: refreshResponse,
        status: refreshResponse.status,
        method: "POST",
        url: refreshResponse.url,
      });
    }

    const newTokenDetails = await refreshResponse.json().then((data) => tokenResponseSchema.parse(data));
    saveTokenDetailsInLocalStorage(newTokenDetails);

    // Clone the original request and update the Authorization header with the new token
    const newHeaders = new Headers(request.headers);
    newHeaders.set("Authorization", `Bearer ${newTokenDetails.access_token}`);
    const newRequest = new Request(request, { headers: newHeaders });

    // Replay the original request with the new token
    const replayedResponse = await fetch(newRequest);
    if (replayedResponse.status === 401) {
      throw new SessionExpiredError(new Error("The retried request was rejected with status 401"));
    }

    return replayedResponse;
  } catch (error) {
    if (error instanceof SessionExpiredError || error instanceof ApiClientError) {
      throw error;
    }

    throw new ApiClientError({
      backend: "backoffice",
      classification: "unknown",
      method: "POST",
      url: `${env.VITE_BACKOFFICE_BASE_URL}/login/refresh`,
      originalError: error instanceof Error ? error : undefined,
    });
  }
}
